/**
 * Check if a workflow status represents a terminal (finished) state.
 */
export function isTerminalStatus(status: string | undefined): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled';
}

/**
 * Persisted approval-gate context, narrowed from a run's generic
 * `metadata` bag (`Record<string, unknown>`). Mirrors the server-side
 * `ApprovalContext` shape, but defined locally so the web package never
 * imports from `@archon/workflows`. Only the fields the client consumes
 * are typed; a minimal `nodeId`/`message` string check is sufficient to
 * confirm a pending gate was persisted.
 */
export interface PersistedApprovalContext {
  nodeId: string;
  message: string;
  captureResponse?: boolean;
  onRejectPrompt?: string;
  options?: unknown;
}

/**
 * Type guard for a persisted approval-gate context. Returns true when `v`
 * is an object carrying string `nodeId` and string `message` — enough to
 * rehydrate a pending approval gate when the live SSE event was missed.
 */
export function isApprovalContext(v: unknown): v is PersistedApprovalContext {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return typeof o.nodeId === 'string' && typeof o.message === 'string';
}
